import React, { useState } from 'react';
import { FaMapMarkerAlt, FaTrash, FaEye, FaSearch, FaTimesCircle, FaSpinner, FaEdit, FaPlus, FaSync } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getLocationByName, updateLocation, deleteLocation, resetAllLocations, syncLocationCounters } from '../../services/locationsApi';
import './ControlPanel.css';
import Spinner from '../Spinner/Spinner';
import AddLocation from "./AddLocation";
import { useLocations } from "../../utils/LocationContext";

const LocationManagement = () => {
    const { locationNames, loadingLocations, refreshLocations } = useLocations();
    // Lista prefetcheada en LocationContext (login/app start).
    const locations = locationNames;
    const loading = loadingLocations;
    const fetchLocations = refreshLocations;
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [locationDetails, setLocationDetails] = useState(null);
    const [editedLocation, setEditedLocation] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState({});
    const [saveLoading, setSaveLoading] = useState(false);
    const [syncLoading, setSyncLoading] = useState({});

    // Para manejar serviceCategoriesCount y productsCategoriesCount
    const [newServiceCategory, setNewServiceCategory] = useState({ key: '', value: 0 });
    const [newProductCategory, setNewProductCategory] = useState({ key: '', value: 0 });

    // Función para obtener detalles de una ubicación
    const handleViewDetails = async (locationName) => {
        setSelectedLocation(locationName);
        setShowModal(true);
        setLoadingDetails(true);

        try {
            const details = await getLocationByName(locationName);
            setLocationDetails(details);
            setEditedLocation({
                ...details,
                serviceCategoriesCount: { ...details.serviceCategoriesCount } || {},
                productsCategoriesCount: { ...details.productsCategoriesCount } || {}
            });
        } catch (error) {
            console.error('Error al cargar detalles de la ubicación:', error);
            toast.error('No se pudieron cargar los detalles de la ubicación');
            setShowModal(false);
        } finally {
            setLoadingDetails(false);
        }
    };

    // Función para eliminar una ubicación
    const handleDeleteLocation = async (locationName) => {
        if (window.confirm(`¿Estás seguro de que deseas eliminar la ubicación ${locationName}?`)) {
            setDeleteLoading(prev => ({ ...prev, [locationName]: true }));
            try {
                await deleteLocation(locationName);
                toast.success('Ubicación eliminada correctamente');
                // Refrescamos el catálogo global de locations.
                fetchLocations();
                if (showModal) setShowModal(false);
            } catch (error) {
                console.error('Error al eliminar ubicación:', error);
                toast.error('No se pudo eliminar la ubicación');
            } finally {
                setDeleteLoading(prev => ({ ...prev, [locationName]: false }));
            }
        }
    };

    // Función para guardar los cambios en la ubicación
    const handleSaveChanges = async () => {
        if (!editedLocation) return;

        setSaveLoading(true);
        try {
            await updateLocation(selectedLocation, editedLocation);
            toast.success('Ubicación actualizada correctamente');
            setShowModal(false);

            // Recargar la lista de ubicaciones para reflejar los cambios
            fetchLocations();
        } catch (error) {
            console.error('Error al actualizar ubicación:', error);
            toast.error('No se pudo actualizar la ubicación');
        } finally {
            setSaveLoading(false);
        }
    };

    // Funciones para manejar cambios en los campos
    const handleInputChange = (field, value) => {
        setEditedLocation(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Funciones para manejar las categorías de servicios
    const handleAddServiceCategory = () => {
        if (newServiceCategory.key.trim() === '') return;
        if (editedLocation.serviceCategoriesCount[newServiceCategory.key]) {
            toast.warning('Esta categoría de servicio ya existe');
            return;
        }

        setEditedLocation(prev => ({
            ...prev,
            serviceCategoriesCount: {
                ...prev.serviceCategoriesCount,
                [newServiceCategory.key]: parseInt(newServiceCategory.value) || 0
            }
        }));

        setNewServiceCategory({ key: '', value: 0 });
    };

    const handleRemoveServiceCategory = (key) => {
        const updatedServiceCategories = { ...editedLocation.serviceCategoriesCount };
        delete updatedServiceCategories[key];

        setEditedLocation(prev => ({
            ...prev,
            serviceCategoriesCount: updatedServiceCategories
        }));
    };

    // Funciones para manejar las categorías de productos
    const handleAddProductCategory = () => {
        if (newProductCategory.key.trim() === '') return;
        if (editedLocation.productsCategoriesCount[newProductCategory.key]) {
            toast.warning('Esta categoría de producto ya existe');
            return;
        }

        setEditedLocation(prev => ({
            ...prev,
            productsCategoriesCount: {
                ...prev.productsCategoriesCount,
                [newProductCategory.key]: parseInt(newProductCategory.value) || 0
            }
        }));

        setNewProductCategory({ key: '', value: 0 });
    };

    const handleRemoveProductCategory = (key) => {
        const updatedProductCategories = { ...editedLocation.productsCategoriesCount };
        delete updatedProductCategories[key];

        setEditedLocation(prev => ({
            ...prev,
            productsCategoriesCount: updatedProductCategories
        }));
    };

    // Filtrar ubicaciones por término de búsqueda
    const filteredLocations = locations.filter(location =>
        location.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleResetAllLocations = async () => {
        if (window.confirm('¿Estás seguro de que deseas reiniciar TODAS las ubicaciones? Esta acción no se puede deshacer.')) {
            try {
                await resetAllLocations();
                toast.success('Todas las ubicaciones han sido reiniciadas correctamente');
                fetchLocations();
            } catch (error) {
                console.error('Error al reiniciar todas las ubicaciones:', error);
                toast.error('No se pudieron reiniciar las ubicaciones');
            }
        }
    };

    // Función para sincronizar los contadores de una ubicación
    const handleSyncCounters = async (locationName) => {
        setSyncLoading(prev => ({ ...prev, [locationName]: true }));
        try {
            const result = await syncLocationCounters(locationName);
            toast.success(`${result.message}`);

            // Actualizar detalles si el modal está abierto y es la ubicación seleccionada
            if (showModal && selectedLocation === locationName) {
                setLocationDetails(result.location);
                setEditedLocation({
                    ...result.location,
                    serviceCategoriesCount: { ...result.location.serviceCategoriesCount } || {},
                    productsCategoriesCount: { ...result.location.productsCategoriesCount } || {}
                });
            }

            // Actualizar la lista de ubicaciones para reflejar los cambios
            fetchLocations();
        } catch (error) {
            console.error('Error al sincronizar contadores:', error);
            toast.error(`No se pudieron sincronizar los contadores. ${error.response?.data || error.message}`);
        } finally {
            setSyncLoading(prev => ({ ...prev, [locationName]: false }));
        }
    };

    return (
        <>
            {loading && (<Spinner/>)}
            <div className="control-panel-row">
                <button
                    className="reset-all-button"
                    onClick={handleResetAllLocations}
                    disabled={loading}
                >
                    {loading ? <FaSpinner /> : 'Reiniciar todas las ubicaciones'}
                </button>

            </div>
            <div className="control-panel-row">
                {/* Columna principal - tabla o lista de categorías */}
                <div className="control-panel-card wide-card">
                    <div className="card-header">
                        <FaMapMarkerAlt /> Gestión de Ubicaciones
                    </div>
                    <div className="card-content">
                        {/* Barra de búsqueda */}
                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ position: 'relative', flex: 1 }}>
                                    <FaSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#aaa' }} />
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Buscar ubicaciones..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{ paddingLeft: '35px' }}
                                    />
                                </div>
                                <button
                                    className="provider-btn provider-btn-outline-primary"
                                    onClick={fetchLocations}
                                    style={{ marginLeft: '10px' }}
                                >
                                    Actualizar
                                </button>
                            </div>
                        </div>

                        {/* Tabla de ubicaciones */}
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '20px' }}>
                                <FaSpinner className="fa-spin" style={{ fontSize: '2rem', color: '#ccc' }} />
                                <p>Cargando ubicaciones...</p>
                            </div>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <table className="providers-table">
                                    <thead>
                                    <tr>
                                        <th style={{ width: '30%' }}>Nombre</th>
                                        <th style={{ width: '70%' }}>Acciones</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {filteredLocations.length > 0 ? (
                                        filteredLocations.map((locationName) => (
                                            <tr key={locationName}>
                                                <td>{locationName}</td>
                                                <td>
                                                    <div className="provider-action-buttons">
                                                        <button
                                                            className="provider-action-btn"
                                                            onClick={() => handleViewDetails(locationName)}
                                                        >
                                                            <FaEye />
                                                            <span className="provider-action-text">Ver/Editar</span>
                                                        </button>
                                                        <button
                                                            className="provider-action-btn"
                                                            style={{ backgroundColor: '#5cb85c' }}
                                                            onClick={() => handleSyncCounters(locationName)}
                                                            disabled={syncLoading[locationName]}
                                                        >
                                                            {syncLoading[locationName] ? (
                                                                <FaSpinner className="fa-spin" />
                                                            ) : (
                                                                <FaSync />
                                                            )}
                                                            <span className="provider-action-text">Sincronizar</span>
                                                        </button>
                                                        <button
                                                            className="provider-action-btn"
                                                            style={{ backgroundColor: '#d9534f' }}
                                                            onClick={() => handleDeleteLocation(locationName)}
                                                            disabled={deleteLoading[locationName]}
                                                        >
                                                            {deleteLoading[locationName] ? (
                                                                <FaSpinner className="fa-spin" />
                                                            ) : (
                                                                <FaTrash />
                                                            )}
                                                            <span className="provider-action-text">Eliminar</span>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="2" style={{ textAlign: 'center', padding: '20px' }}>
                                                <FaTimesCircle style={{ fontSize: '1.5rem', color: '#aaa', marginBottom: '10px' }} />
                                                <p>No se encontraron ubicaciones</p>
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
                {/* Columna secundaria - agregar ubicación */}
                <AddLocation onAdded={fetchLocations}/>

                {/* Modal de detalles y edición de ubicación */}
                {showModal && (
                    <div className="provider-modal-backdrop">
                        <div className="provider-modal-container provider-location-modal">
                            <div className="provider-modal-header">
                                <h3>Detalles de la Ubicación</h3>
                                <button className="provider-modal-close" onClick={() => setShowModal(false)}>
                                    ✕
                                </button>
                            </div>
                            <div className="provider-modal-body">
                                {loadingDetails ? (
                                    <div style={{ textAlign: 'center', padding: '20px' }}>
                                        <FaSpinner className="fa-spin" style={{ fontSize: '2rem', color: '#ccc' }} />
                                        <p>Cargando detalles...</p>
                                    </div>
                                ) : editedLocation ? (
                                    <div className="provider-details-columns">
                                        <div className="provider-details-column">
                                            <h4>Información de la Ubicación</h4>
                                            <p><strong>Nombre:</strong> {selectedLocation}</p>

                                            <div className="form-group">
                                                <label htmlFor="zone">Zona:</label>
                                                <input
                                                    type="text"
                                                    id="zone"
                                                    className="form-control"
                                                    value={editedLocation.zone || ''}
                                                    onChange={(e) => handleInputChange('zone', e.target.value)}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="code">Código:</label>
                                                <input
                                                    type="text"
                                                    id="code"
                                                    className="form-control"
                                                    value={editedLocation.code || ''}
                                                    onChange={(e) => handleInputChange('code', e.target.value)}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="order">Orden:</label>
                                                <input
                                                    type="number"
                                                    id="order"
                                                    className="form-control"
                                                    value={editedLocation.order || 1}
                                                    onChange={(e) => handleInputChange('order', parseInt(e.target.value) || 1)}
                                                    min="1"
                                                />
                                                <small className="form-text">Orden de aparición en la lista (menor número aparece primero)</small>
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="serviceProviders">Proveedores de Servicios:</label>
                                                <input
                                                    type="number"
                                                    id="serviceProviders"
                                                    className="form-control"
                                                    value={editedLocation.serviceProviders || 0}
                                                    onChange={(e) => handleInputChange('serviceProviders', parseInt(e.target.value) || 0)}
                                                    min="0"
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="productsProviders">Proveedores de Productos:</label>
                                                <input
                                                    type="number"
                                                    id="productsProviders"
                                                    className="form-control"
                                                    value={editedLocation.productsProviders || 0}
                                                    onChange={(e) => handleInputChange('productsProviders', parseInt(e.target.value) || 0)}
                                                    min="0"
                                                />
                                            </div>

                                            <h4>Categorías de Servicios</h4>
                                            <div className="categories-editor">
                                                <div className="categories-list">
                                                    {Object.keys(editedLocation.serviceCategoriesCount || {}).length > 0 ? (
                                                        <ul className="category-items">
                                                            {Object.entries(editedLocation.serviceCategoriesCount || {}).map(([key, value]) => (
                                                                <li key={key} className="category-item">
                                                                    <span className="category-name">{key}</span>
                                                                    <span className="category-value">{value}</span>
                                                                    <button
                                                                        className="category-remove-btn"
                                                                        onClick={() => handleRemoveServiceCategory(key)}
                                                                    >
                                                                        <FaTimesCircle />
                                                                    </button>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        <p className="no-categories">No hay categorías de servicios definidas</p>
                                                    )}
                                                </div>

                                                <div className="add-category-form">
                                                    <div className="category-input-group">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Categoría..."
                                                            value={newServiceCategory.key}
                                                            onChange={(e) => setNewServiceCategory({...newServiceCategory, key: e.target.value})}
                                                        />
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            placeholder="Cantidad"
                                                            value={newServiceCategory.value}
                                                            onChange={(e) => setNewServiceCategory({...newServiceCategory, value: e.target.value})}
                                                            min="0"
                                                            style={{ width: '100px' }}
                                                        />
                                                        <button
                                                            className="provider-btn provider-btn-sm provider-btn-primary"
                                                            onClick={handleAddServiceCategory}
                                                        >
                                                            <FaPlus />
                                                            <span>Añadir</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            <h4>Categorías de Productos</h4>
                                            <div className="categories-editor">
                                                <div className="categories-list">
                                                    {Object.keys(editedLocation.productsCategoriesCount || {}).length > 0 ? (
                                                        <ul className="category-items">
                                                            {Object.entries(editedLocation.productsCategoriesCount || {}).map(([key, value]) => (
                                                                <li key={key} className="category-item">
                                                                    <span className="category-name">{key}</span>
                                                                    <span className="category-value">{value}</span>
                                                                    <button
                                                                        className="category-remove-btn"
                                                                        onClick={() => handleRemoveProductCategory(key)}
                                                                    >
                                                                        <FaTimesCircle />
                                                                    </button>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        <p className="no-categories">No hay categorías de productos definidas</p>
                                                    )}
                                                </div>

                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <p>No se pudieron cargar los detalles de la ubicación.</p>
                                    )}
                                </div>
                                <div className="provider-modal-footer">
                                    <button
                                        className="provider-btn provider-btn-outline"
                                        onClick={() => setShowModal(false)}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        className="provider-btn provider-btn-primary"
                                        onClick={() => handleDeleteLocation(selectedLocation)}
                                        style={{ backgroundColor: '#d9534f', marginRight: '8px' }}
                                    >
                                        <FaTrash />
                                        <span className="provider-btn-text">Eliminar</span>
                                    </button>
                                    <button
                                        className="provider-btn provider-btn-primary"
                                        onClick={handleSaveChanges}
                                        disabled={saveLoading}
                                    >
                                        {saveLoading ? (
                                            <FaSpinner className="fa-spin" />
                                        ) : (
                                            <FaEdit />
                                        )}
                                        <span className="provider-btn-text">Guardar Cambios</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </>
    );
};

export default LocationManagement;
